import React, { useEffect, useState } from 'react';
import { createUseStyles } from 'react-jss';
import defs from './definitions';
import { RevealEffectConfig, RevealEffectStartStyles } from 'types';

export const startStyles = Object.keys(defs).reduce((styles, name) => {
  return {
    ...styles,
    [name]: defs[name].start
  };
}, {}) as RevealEffectStartStyles;

const useStyles = createUseStyles(
  Object.keys(defs).reduce((styles, name) => {
    return {
      ...styles,
      ...defs[name].animation
    };
  }, {})
);

interface IProps {
  revealEffects: RevealEffectConfig[];
  execute?: boolean;
}

const RevealEffects: React.FunctionComponent<IProps> = ({ execute = true, revealEffects, children }) => {
  const styles = useStyles();
  const [hasRevealed, setHasRevealed] = useState<string[]>([]);

  function revealElement(effect: RevealEffectConfig): void {
    const { selector, revealEffect } = effect;
    // Immediately return if this selector has already been animated
    if (hasRevealed.indexOf(selector) >= 0) return;

    // Get the element and apply the revealEffect if it's in view
    const element = document.querySelector(selector);
    if (element && defs[revealEffect].shouldReveal(element)) {
      defs[revealEffect].reveal(element, styles);

      // Add the selector to the list of selectors which have been animated
      setHasRevealed([...hasRevealed, selector]);
    }
  }

  function revealElementChildren(effect: RevealEffectConfig): void {
    const { selector, revealEffect } = effect;
    // Immediately return if this selector's children have
    // already been fully animated
    if (hasRevealed.indexOf(selector) >= 0) return;

    // Get the elements children
    const element = document.querySelector(selector);
    const children = element?.children;

    // Track when all children have been revealed.
    // This gets set to false if there's a child
    // that shouldn't be revealed yet
    let allChildrenRevealed = true;

    if (!children || children.length === 0) return;

    // Loop over all the children and call `reveal` in
    // a setTimeout so that the animations for the children cascades
    let animationElementTiming = 0;
    for (let i = 0; i < children.length; i++) {
      const child = children[i];
      // Only call reveal on the child and increase the animationElementTiming value
      // if the element should be revealed and the element doesn't have the animation class
      if (!child.classList.contains(styles[revealEffect])) {
        if (defs[revealEffect].shouldReveal(child)) {
          setTimeout(() => {
            defs[revealEffect].reveal(child, styles);
          }, (animationElementTiming + 1) * 100);
          animationElementTiming++;
        } else {
          allChildrenRevealed = false;
        }
      }
    }

    if (allChildrenRevealed) setHasRevealed([...hasRevealed, selector]);
  }

  function reveal(): void {
    // Loop through each animation on scroll to determine when
    revealEffects.forEach((effect) => {
      if (effect.cascadeChildren) {
        revealElementChildren(effect);
      } else {
        revealElement(effect);
      }
    });
  }

  useEffect(() => {
    // Only apply the revealEffect and attach the scroll handler if
    // there are still revealEffect which have not been animated yet
    // const throttledAddrevealEffectWhenInView = throttle(addrevealEffectWhenInView, 200);
    if (execute && hasRevealed.length < revealEffects.length) {
      reveal();
      window.addEventListener('scroll', reveal, { passive: true });
    }
    return () => {
      if (execute && hasRevealed.length < revealEffects.length) {
        window.removeEventListener('scroll', reveal);
      }
    };
  }, [execute, hasRevealed.length]);

  return <>{children}</>;
};

export default RevealEffects;
